import {
  FeatureRequest,
  FeatureVote,
  FeatureComment,
  CreateFeatureRequestDto,
  CreateFeatureCommentDto,
  FeatureRequestFilters,
  FeatureStatus,
  FeatureCategory,
  FeatureRequestError,
  FeatureRequestServiceResponse,
  FeatureRequestWithVote,
  FeatureRequestAnalytics,
  VoteValidation,
  DuplicateDetectionResult,
} from "../types/featureRequests";
import { logger } from "../lib/logger";
import { supabase } from "../lib/supabase";
import i18n from "@core/i18n";

/**
 * Wrap a promise with a timeout. Rejects with a FeatureRequestError if the
 * underlying promise doesn't resolve in `ms` milliseconds. Prevents infinite
 * spinners if Supabase hangs (cf. bug triage #13).
 */
function withTimeout<T>(
  promise: PromiseLike<T>,
  ms: number,
  label: string,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new FeatureRequestError(
          i18n.t("featureRequest.error.timeout", { ms, label }),
          "TIMEOUT",
        ),
      );
    }, ms);
    Promise.resolve(promise).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

export class FeatureRequestService {
  private static instance: FeatureRequestService;
  private supabase;

  private constructor() {
    this.supabase = supabase;
  }

  public static getInstance(): FeatureRequestService {
    if (!FeatureRequestService.instance) {
      FeatureRequestService.instance = new FeatureRequestService();
    }
    return FeatureRequestService.instance;
  }

  /**
   * Create a new feature request
   */
  async createFeatureRequest(
    data: CreateFeatureRequestDto,
    userId?: string,
  ): Promise<FeatureRequestServiceResponse<FeatureRequest>> {
    try {
      // Validate input
      if (!data.title?.trim()) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.titleRequired"),
          "MISSING_TITLE",
        );
      }
      if (!data.description?.trim()) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.descriptionRequired"),
          "MISSING_DESCRIPTION",
        );
      }
      if (data.title.length > 200) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.titleTooLong"),
          "TITLE_TOO_LONG",
        );
      }
      if (data.description.length > 5000) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.descriptionTooLong"),
          "DESCRIPTION_TOO_LONG",
        );
      }

      // Check for duplicates (optional - can be expensive)
      const duplicateCheck = await this.detectDuplicates(
        data.title,
        data.description,
      );
      if (duplicateCheck.hasDuplicates && duplicateCheck.confidence > 0.9) {
        return {
          success: false,
          error: new FeatureRequestError(
            i18n.t("featureRequest.error.duplicateFound"),
            "DUPLICATE_FOUND",
            duplicateCheck.matches,
          ),
        };
      }

      const currentLanguage = i18n.language || "en";
      const insertData = {
        title: data.title.trim(),
        description: data.description.trim(),
        title_cache: data.title.trim(),
        description_cache: data.description.trim(),
        default_language: currentLanguage,
        category: data.category || "other",
        created_by: userId,
        status: "proposed" as FeatureStatus,
        priority: 0,
        votes_count: 0,
      };

      const { data: featureRequest, error } = await this.supabase
        .from("feature_requests")
        .insert(insertData)
        .select()
        .single();

      if (error) {
        logger.error("Failed to create feature request", "featureRequest", {
          error,
          data,
        });
        // Le trigger enforce_feature_request_rate_limit utilise
        // ERRCODE P0001 et préfixe le message avec 'RATE_LIMIT_EXCEEDED:'
        // Cf. supabase/migrations/20260415_feature_requests_rate_limit.sql
        if (error.message?.includes("RATE_LIMIT_EXCEEDED")) {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.rateLimited"),
            "RATE_LIMITED",
            error,
          );
        }
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.createFailed"),
          "CREATE_FAILED",
          error,
        );
      }

      // Insert translation for current language
      try {
        await this.supabase.from("feature_requests_translations").insert({
          feature_request_id: featureRequest.id,
          language: currentLanguage,
          title: data.title.trim(),
          description: data.description.trim(),
          created_by: userId,
        });
      } catch (translationError) {
        logger.warn(
          "Failed to create translation for new feature request",
          "featureRequest",
          {
            error: translationError,
            featureId: featureRequest.id,
            language: currentLanguage,
          },
        );
        // Don't fail the main operation if translation insert fails
      }

      logger.info("Feature request created successfully", "featureRequest", {
        featureId: featureRequest.id,
        title: featureRequest.title,
      });

      // Send notification about new feature request
      try {
        const { FeatureRequestNotificationService } =
          await import("./FeatureRequestNotificationService");
        const notificationService =
          FeatureRequestNotificationService.getInstance();
        await notificationService.notifyNewFeatureRequest(featureRequest);
      } catch (notificationError) {
        logger.warn(
          "Failed to send notification for new feature request",
          "featureRequest",
          {
            error: notificationError,
            featureId: featureRequest.id,
          },
        );
        // Don't fail the main operation if notification fails
      }

      // Check for duplicates (async, doesn't block creation)
      this.checkForDuplicatesAsync(featureRequest).catch((error) => {
        logger.warn("Failed to check for duplicates", "featureRequest", {
          error,
          featureId: featureRequest.id,
        });
      });

      return {
        success: true,
        data: featureRequest,
      };
    } catch (error) {
      logger.error("Error creating feature request", "featureRequest", {
        error,
        data,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.createUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Get feature requests with filters, language-aware
   */
  async getFeatureRequests(
    filters: FeatureRequestFilters = {},
    userId?: string,
    language?: string,
  ): Promise<FeatureRequestServiceResponse<FeatureRequestWithVote[]>> {
    try {
      // Default to current i18n language if not provided
      const lang = language || i18n.language || "en";

      let query = this.supabase
        .from("feature_requests")
        .select(
          `
          *,
          feature_votes(id, user_id),
          feature_requests_translations(id, language, title, description)
        `,
        )
        .is("deleted_at", null);

      // Apply filters
      if (filters.status) {
        query = query.eq("status", filters.status);
      }
      if (filters.category) {
        query = query.eq("category", filters.category);
      }
      if (filters.search) {
        // Search uses cache columns (always available as fallback)
        query = query.or(
          `title_cache.ilike.%${filters.search}%,description_cache.ilike.%${filters.search}%`,
        );
      }

      // Apply sorting
      const sortBy = filters.sort_by || "created_at";
      const sortOrder = filters.sort_order || "desc";
      query = query.order(sortBy, { ascending: sortOrder === "asc" });

      // Apply pagination
      if (filters.limit) {
        query = query.limit(filters.limit);
      }
      if (filters.offset) {
        query = query.range(
          filters.offset,
          filters.offset + (filters.limit || 10) - 1,
        );
      }

      const { data: requests, error } = await withTimeout<{
        data: any[] | null;
        error: any;
      }>(query as any, 10000, "getFeatureRequests");

      if (error) {
        logger.error("Failed to fetch feature requests", "featureRequest", {
          error,
          filters,
          language: lang,
        });
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.fetchListFailed"),
          "FETCH_FAILED",
          error,
        );
      }

      // Transform data: pick title/description from translations or fall back to cache
      const transformedRequests: FeatureRequestWithVote[] = requests.map(
        (request) => {
          const translation = request.feature_requests_translations?.find(
            (t: any) => t.language === lang,
          );

          return {
            ...request,
            title: translation?.title || request.title_cache || request.title,
            description:
              translation?.description ||
              request.description_cache ||
              request.description,
            user_has_voted: userId
              ? request.feature_votes.some(
                  (vote: FeatureVote) => vote.user_id === userId,
                )
              : false,
            user_vote: userId
              ? request.feature_votes.find(
                  (vote: FeatureVote) => vote.user_id === userId,
                )
              : undefined,
            feature_votes: undefined, // Remove the nested votes array
            feature_requests_translations: undefined, // Remove translations array
          };
        },
      );

      return {
        success: true,
        data: transformedRequests,
      };
    } catch (error) {
      logger.error("Error fetching feature requests", "featureRequest", {
        error,
        filters,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.fetchListUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Get a single feature request by ID, language-aware
   */
  async getFeatureRequest(
    id: string,
    userId?: string,
    language?: string,
  ): Promise<FeatureRequestServiceResponse<FeatureRequestWithVote>> {
    try {
      const lang = language || i18n.language || "en";

      const { data: request, error } = await this.supabase
        .from("feature_requests")
        .select(
          `
          *,
          feature_votes(id, user_id),
          feature_requests_translations(id, language, title, description)
        `,
        )
        .eq("id", id)
        .is("deleted_at", null)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.notFound"),
            "NOT_FOUND",
          );
        }
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.fetchFailed"),
          "FETCH_FAILED",
          error,
        );
      }

      // Pick translation or fall back to cache
      const translation = request.feature_requests_translations?.find(
        (t: any) => t.language === lang,
      );

      const transformedRequest: FeatureRequestWithVote = {
        ...request,
        title: translation?.title || request.title_cache || request.title,
        description:
          translation?.description ||
          request.description_cache ||
          request.description,
        user_has_voted: userId
          ? request.feature_votes.some(
              (vote: FeatureVote) => vote.user_id === userId,
            )
          : false,
        user_vote: userId
          ? request.feature_votes.find(
              (vote: FeatureVote) => vote.user_id === userId,
            )
          : undefined,
        feature_votes: undefined,
        feature_requests_translations: undefined,
      };

      return {
        success: true,
        data: transformedRequest,
      };
    } catch (error) {
      logger.error("Error fetching feature request", "featureRequest", {
        error,
        id,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.fetchUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Vote for a feature request
   */
  async voteForFeature(
    featureId: string,
    userId: string,
  ): Promise<FeatureRequestServiceResponse<FeatureVote>> {
    try {
      // Validate user can vote
      const validation = await this.validateVote(featureId, userId);
      if (!validation.valid) {
        return {
          success: false,
          error: new FeatureRequestError(
            validation.error || i18n.t("featureRequest.error.cannotVote"),
            "VOTE_NOT_ALLOWED",
          ),
        };
      }

      // Check if feature request exists
      const { data: feature, error: featureError } = await this.supabase
        .from("feature_requests")
        .select("id, status")
        .eq("id", featureId)
        .is("deleted_at", null)
        .single();

      if (featureError || !feature) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.notFound"),
          "NOT_FOUND",
        );
      }

      // Don't allow voting on completed or rejected features
      if (feature.status === "completed" || feature.status === "rejected") {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.voteClosedStatus"),
          "INVALID_STATUS",
        );
      }

      // Insert vote
      const { data: vote, error } = await this.supabase
        .from("feature_votes")
        .insert({
          feature_request_id: featureId,
          user_id: userId,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.alreadyVoted"),
            "ALREADY_VOTED",
          );
        }
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.voteFailed"),
          "VOTE_FAILED",
          error,
        );
      }

      logger.info("Vote recorded successfully", "featureRequest", {
        featureId,
        userId,
        voteId: vote.id,
      });

      return {
        success: true,
        data: vote,
      };
    } catch (error) {
      logger.error("Error voting for feature", "featureRequest", {
        error,
        featureId,
        userId,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.voteUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Remove vote for a feature request
   */
  async removeVote(
    featureId: string,
    userId: string,
  ): Promise<FeatureRequestServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("feature_votes")
        .delete()
        .eq("feature_request_id", featureId)
        .eq("user_id", userId);

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.removeVoteFailed"),
          "REMOVE_VOTE_FAILED",
          error,
        );
      }

      logger.info("Vote removed successfully", "featureRequest", {
        featureId,
        userId,
      });

      return {
        success: true,
      };
    } catch (error) {
      logger.error("Error removing vote", "featureRequest", {
        error,
        featureId,
        userId,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.removeVoteUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * List comments for a feature request, du plus ancien au plus récent.
   * Les noms d'auteur sont résolus via une 2e requête sur `profiles`
   * (pas de FK déclarée entre feature_comments.user_id et profiles.id,
   * donc le join embedded Supabase ne marche pas).
   */
  async listComments(
    featureId: string,
  ): Promise<FeatureRequestServiceResponse<FeatureComment[]>> {
    try {
      const { data: rows, error } = await this.supabase
        .from("feature_comments")
        .select(
          "id, feature_request_id, user_id, content, created_at, updated_at",
        )
        .eq("feature_request_id", featureId)
        .order("created_at", { ascending: true });

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.commentsFetchFailed"),
          "COMMENTS_FETCH_FAILED",
          error,
        );
      }

      // Résolution des noms d'auteur en une seule requête
      const userIds = Array.from(
        new Set((rows || []).map((r: any) => r.user_id).filter(Boolean)),
      );
      const profilesById: Record<string, { name?: string; email?: string }> =
        {};
      if (userIds.length > 0) {
        const { data: profiles } = await this.supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .in("id", userIds);
        if (profiles) {
          for (const p of profiles as Array<{
            id: string;
            first_name?: string;
            last_name?: string;
            email?: string;
          }>) {
            const name =
              [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email;
            profilesById[p.id] = { name, email: p.email };
          }
        }
      }

      const comments: FeatureComment[] = (rows || []).map((row: any) => ({
        id: row.id,
        feature_request_id: row.feature_request_id,
        user_id: row.user_id,
        content: row.content,
        created_at: row.created_at,
        updated_at: row.updated_at,
        author_name: profilesById[row.user_id]?.name,
        author_email: profilesById[row.user_id]?.email,
      }));

      return { success: true, data: comments };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.commentsFetchUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Create a new comment on a feature request. Requires auth.
   */
  async addComment(
    data: CreateFeatureCommentDto,
    userId: string,
  ): Promise<FeatureRequestServiceResponse<FeatureComment>> {
    try {
      const trimmed = data.content?.trim() ?? "";
      if (!trimmed) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.commentEmpty"),
          "EMPTY_COMMENT",
        );
      }
      if (trimmed.length > 2000) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.commentTooLong"),
          "COMMENT_TOO_LONG",
        );
      }

      const { data: inserted, error } = await this.supabase
        .from("feature_comments")
        .insert({
          feature_request_id: data.feature_request_id,
          user_id: userId,
          content: trimmed,
        })
        .select()
        .single();

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.commentCreateFailed"),
          "COMMENT_CREATE_FAILED",
          error,
        );
      }

      return { success: true, data: inserted };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.commentCreateUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Delete a comment. Autorisé par RLS à l'auteur ; les admins peuvent le
   * faire via une future policy si nécessaire.
   */
  async deleteComment(
    commentId: string,
  ): Promise<FeatureRequestServiceResponse<void>> {
    try {
      const { error } = await this.supabase
        .from("feature_comments")
        .delete()
        .eq("id", commentId);

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.commentDeleteFailed"),
          "COMMENT_DELETE_FAILED",
          error,
        );
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.commentDeleteUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Update a feature request as its author. Autorisé par RLS seulement si
   * auth.uid() = created_by. On restreint aussi côté service aux statuts
   * où l'édition a du sens (proposed, under_review). Cf. Ticket P2.4.
   */
  async updateFeatureRequestByAuthor(
    featureId: string,
    updates: {
      title?: string;
      description?: string;
      category?: FeatureCategory;
    },
  ): Promise<FeatureRequestServiceResponse<FeatureRequest>> {
    try {
      // Validate
      if (updates.title !== undefined) {
        if (!updates.title.trim()) {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.titleRequired"),
            "MISSING_TITLE",
          );
        }
        if (updates.title.length > 200) {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.titleTooLong"),
            "TITLE_TOO_LONG",
          );
        }
      }
      if (updates.description !== undefined) {
        if (!updates.description.trim()) {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.descriptionRequired"),
            "MISSING_DESCRIPTION",
          );
        }
        if (updates.description.length > 5000) {
          throw new FeatureRequestError(
            i18n.t("featureRequest.error.descriptionTooLong"),
            "DESC_TOO_LONG",
          );
        }
      }

      // Check statut : seuls proposed/under_review sont éditables par l'auteur
      const { data: existing, error: fetchError } = await this.supabase
        .from("feature_requests")
        .select("status")
        .eq("id", featureId)
        .single();

      if (fetchError || !existing) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.notFound"),
          "NOT_FOUND",
          fetchError,
        );
      }

      if (
        existing.status !== "proposed" &&
        existing.status !== "under_review"
      ) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.statusLocked"),
          "STATUS_LOCKED",
        );
      }

      const patch: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (updates.title !== undefined) patch.title = updates.title.trim();
      if (updates.description !== undefined)
        patch.description = updates.description.trim();
      if (updates.category !== undefined) patch.category = updates.category;

      const { data: updated, error } = await this.supabase
        .from("feature_requests")
        .update(patch)
        .eq("id", featureId)
        .select()
        .single();

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.updateFailed"),
          "UPDATE_FAILED",
          error,
        );
      }

      return { success: true, data: updated };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.updateUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Retourne les duplicate detections en attente de review (admin).
   * Appelle la fonction SQL get_pending_duplicate_detections qui fait le
   * join avec feature_requests et filtre confidence >= 0.8.
   * Cf. Ticket P2.6.
   */
  async getPendingDuplicateDetections(): Promise<
    FeatureRequestServiceResponse<
      Array<{
        id: string;
        feature_request_id: string;
        feature_title: string;
        feature_category: string | null;
        confidence: number;
        duplicate_count: number;
        detected_at: string;
        duplicate_matches: unknown;
      }>
    >
  > {
    try {
      const { data, error } = await this.supabase.rpc(
        "get_pending_duplicate_detections",
      );
      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.pendingDetectionsFetchFailed"),
          "FETCH_FAILED",
          error,
        );
      }
      return { success: true, data: data || [] };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.pendingDetectionsUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Marque une duplicate detection comme revue par l'admin.
   * Si isDuplicate=true, le feature_request est automatiquement passé en
   * "rejected" côté trigger SQL.
   */
  async reviewDuplicateDetection(
    detectionId: string,
    isDuplicate: boolean,
    adminNotes?: string,
  ): Promise<FeatureRequestServiceResponse<void>> {
    try {
      const { error } = await this.supabase.rpc("review_duplicate_detection", {
        detection_id: detectionId,
        is_duplicate: isDuplicate,
        admin_notes: adminNotes ?? null,
      });
      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.reviewDetectionFailed"),
          "REVIEW_FAILED",
          error,
        );
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.reviewDetectionUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Update admin-only fields on a feature request : priority,
   * target_version, estimated_days, github_issue_url, admin_notes.
   * RLS garantit que seul un admin (cf. public.is_admin()) peut le faire.
   * Cf. Ticket P2.5.
   */
  async updateFeatureAdminFields(
    featureId: string,
    fields: {
      priority?: number | null;
      target_version?: string | null;
      estimated_days?: number | null;
      github_issue_url?: string | null;
      admin_notes?: string | null;
    },
  ): Promise<FeatureRequestServiceResponse<FeatureRequest>> {
    try {
      if (
        fields.priority !== undefined &&
        fields.priority !== null &&
        (fields.priority < 0 || fields.priority > 10)
      ) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.invalidPriority"),
          "INVALID_PRIORITY",
        );
      }

      const patch: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (fields.priority !== undefined) patch.priority = fields.priority;
      if (fields.target_version !== undefined)
        patch.target_version = fields.target_version;
      if (fields.estimated_days !== undefined)
        patch.estimated_days = fields.estimated_days;
      if (fields.github_issue_url !== undefined)
        patch.github_issue_url = fields.github_issue_url;
      if (fields.admin_notes !== undefined)
        patch.admin_notes = fields.admin_notes;

      const { data, error } = await this.supabase
        .from("feature_requests")
        .update(patch)
        .eq("id", featureId)
        .select()
        .single();

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.updateAdminFailed"),
          "UPDATE_FAILED",
          error,
        );
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.updateAdminUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Soft-delete a feature request (admin only). Marks deleted_at so the item
   * disappears from all public queries (which filter deleted_at IS NULL) but
   * the row and its votes/comments are preserved in DB for history.
   * RLS (policy "Admins can update any feature request") enforces admin-only.
   */
  async softDeleteFeatureRequest(
    featureId: string,
  ): Promise<FeatureRequestServiceResponse<FeatureRequest>> {
    try {
      const now = new Date().toISOString();
      const { data, error } = await this.supabase
        .from("feature_requests")
        .update({ deleted_at: now, updated_at: now })
        .eq("id", featureId)
        .select()
        .single();

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.deleteFailed"),
          "DELETE_FAILED",
          error,
        );
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.deleteUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Update feature request status (admin only)
   */
  async updateFeatureStatus(
    featureId: string,
    status: FeatureStatus,
    adminNotes?: string,
  ): Promise<FeatureRequestServiceResponse<FeatureRequest>> {
    try {
      // Get the current feature to check old status
      const { data: currentFeature, error: fetchError } = await this.supabase
        .from("feature_requests")
        .select("*")
        .eq("id", featureId)
        .single();

      if (fetchError) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.fetchFailed"),
          "FETCH_FAILED",
          fetchError,
        );
      }

      const oldStatus = currentFeature.status;

      const updateData: Partial<FeatureRequest> = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (adminNotes) {
        updateData.admin_notes = adminNotes;
      }

      const { data: feature, error } = await this.supabase
        .from("feature_requests")
        .update(updateData)
        .eq("id", featureId)
        .select()
        .single();

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.statusUpdateFailed"),
          "UPDATE_FAILED",
          error,
        );
      }

      logger.info("Feature status updated successfully", "featureRequest", {
        featureId,
        oldStatus,
        newStatus: status,
        adminNotes,
      });

      // Send notification about status change
      if (oldStatus !== status) {
        try {
          const { FeatureRequestNotificationService } =
            await import("./FeatureRequestNotificationService");
          const notificationService =
            FeatureRequestNotificationService.getInstance();
          await notificationService.notifyStatusChange(
            feature,
            oldStatus,
            status,
          );
        } catch (notificationError) {
          logger.warn(
            "Failed to send notification for status change",
            "featureRequest",
            {
              error: notificationError,
              featureId,
              oldStatus,
              newStatus: status,
            },
          );
          // Don't fail the main operation if notification fails
        }
      }

      return {
        success: true,
        data: feature,
      };
    } catch (error) {
      logger.error("Error updating feature status", "featureRequest", {
        error,
        featureId,
        status,
      });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.statusUpdateUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Get feature request analytics
   */
  async getFeatureRequestAnalytics(): Promise<
    FeatureRequestServiceResponse<FeatureRequestAnalytics>
  > {
    try {
      const { data: requests, error } = await this.supabase
        .from("feature_requests")
        .select(
          "id, title, description, priority, status, category, votes_count, created_at, updated_at",
        )
        .is("deleted_at", null);

      // Type assertion pour les données retournées par Supabase
      const typedRequests = requests as Array<{
        id: string;
        title: string;
        description: string;
        priority: number;
        status: FeatureStatus;
        category: string;
        votes_count: number;
        created_at: string;
        updated_at: string;
      }>;

      if (error) {
        throw new FeatureRequestError(
          i18n.t("featureRequest.error.analyticsFailed"),
          "ANALYTICS_FAILED",
          error,
        );
      }

      // Calculate analytics
      const total = typedRequests.length;
      const byStatus = typedRequests.reduce(
        (acc, req) => {
          const status = req.status as FeatureStatus;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {} as Record<FeatureStatus, number>,
      );

      const byCategory = typedRequests.reduce(
        (acc, req) => {
          const cat = req.category || "other";
          acc[cat] = (acc[cat] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      const totalVotes = typedRequests.reduce(
        (sum, req) => sum + req.votes_count,
        0,
      );
      const averageVotes = total > 0 ? totalVotes / total : 0;

      const completed = typedRequests.filter(
        (req) => req.status === "completed",
      );
      const completionRate = total > 0 ? (completed.length / total) * 100 : 0;

      const averageTimeToCompletion =
        completed.length > 0
          ? completed.reduce(
              (sum, req) =>
                sum +
                (new Date(req.updated_at).getTime() -
                  new Date(req.created_at).getTime()),
              0,
            ) / completed.length
          : 0;

      const topVoted = typedRequests
        .sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0))
        .slice(0, 10);

      const recentActivity = typedRequests
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        )
        .slice(0, 10);

      const analytics: FeatureRequestAnalytics = {
        total_requests: total,
        by_status: byStatus,
        by_category: byCategory,
        average_votes_per_request: averageVotes,
        top_voted: topVoted,
        recent_activity: recentActivity,
        completion_rate: completionRate,
        average_time_to_completion: averageTimeToCompletion,
      };

      return {
        success: true,
        data: analytics,
      };
    } catch (error) {
      logger.error("Error fetching analytics", "featureRequest", { error });
      return {
        success: false,
        error:
          error instanceof FeatureRequestError
            ? error
            : new FeatureRequestError(
                i18n.t("featureRequest.error.analyticsUnexpected"),
                "UNEXPECTED_ERROR",
                error,
              ),
      };
    }
  }

  /**
   * Validate if user can vote
   */
  private async validateVote(
    featureId: string,
    userId: string,
  ): Promise<VoteValidation> {
    try {
      // Check if user already voted
      const { data: existingVote } = await this.supabase
        .from("feature_votes")
        .select()
        .eq("feature_request_id", featureId)
        .eq("user_id", userId)
        .single();

      if (existingVote) {
        return {
          valid: false,
          error: i18n.t("featureRequest.error.alreadyVoted"),
          existing_vote: existingVote,
        };
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: i18n.t("featureRequest.error.voteValidationFailed"),
      };
    }
  }

  /**
   * Basic duplicate detection (can be enhanced with AI)
   */
  private async detectDuplicates(
    title: string,
    description: string,
  ): Promise<DuplicateDetectionResult> {
    try {
      // Simple keyword-based detection for now
      // TODO: Enhance with semantic similarity using embeddings
      const keywords = title
        .toLowerCase()
        .split(" ")
        .filter((word) => word.length > 3);

      if (keywords.length === 0) {
        return { hasDuplicates: false, matches: [], confidence: 0 };
      }

      const { data: similarRequests } = await this.supabase
        .from("feature_requests")
        .select()
        .is("deleted_at", null)
        .or(
          `title.ilike.%${keywords.join("%")}%,description.ilike.%${keywords.join("%")}%`,
        )
        .limit(5);

      if (!similarRequests || similarRequests.length === 0) {
        return { hasDuplicates: false, matches: [], confidence: 0 };
      }

      // Calculate simple similarity score
      const matches = similarRequests
        .map((request) => {
          const titleSimilarity = this.calculateStringSimilarity(
            title,
            request.title,
          );
          const descSimilarity = this.calculateStringSimilarity(
            description,
            request.description,
          );
          const similarity = Math.max(titleSimilarity, descSimilarity);

          return {
            feature: request,
            similarity,
            reasons:
              similarity > 0.7
                ? [i18n.t("featureRequest.duplicate.reasonSimilar")]
                : [],
          };
        })
        .filter((match) => match.similarity > 0.7);

      return {
        hasDuplicates: matches.length > 0,
        matches,
        confidence:
          matches.length > 0
            ? Math.max(...matches.map((m) => m.similarity))
            : 0,
      };
    } catch (error) {
      logger.error("Error detecting duplicates", "featureRequest", { error });
      return { hasDuplicates: false, matches: [], confidence: 0 };
    }
  }

  /**
   * Simple string similarity calculation (can be enhanced)
   */
  private calculateStringSimilarity(str1: string, str2: string): number {
    const s1 = str1.toLowerCase();
    const s2 = str2.toLowerCase();
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;

    if (longer.length === 0) return 1.0;

    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  /**
   * Levenshtein distance calculation
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1,
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }

  /**
   * Async duplicate check that doesn't block the main operation
   */
  private async checkForDuplicatesAsync(
    featureRequest: FeatureRequest,
  ): Promise<void> {
    const duplicateResult = await this.detectDuplicates(
      featureRequest.title,
      featureRequest.description,
    );

    if (duplicateResult.hasDuplicates && duplicateResult.confidence > 0.8) {
      // Écriture via edge function en service_role : la policy INSERT sur
      // feature_duplicate_detections est verrouillée à service_role pour
      // fermer la surface d'attaque (lint Supabase 0024).
      await this.supabase.functions.invoke("feature-system-log", {
        body: {
          action: "duplicate_detection",
          feature_request_id: featureRequest.id,
          duplicate_data: duplicateResult,
        },
      });

      logger.info("High-confidence duplicates detected", "featureRequest", {
        featureId: featureRequest.id,
        duplicateCount: duplicateResult.matches.length,
        confidence: duplicateResult.confidence,
      });
    }
  }
}
